// The data-access seam.
//
// Every read and write in this app goes through this module. Views never call
// claude.use(), never call fetch(), never touch localStorage for shared data. That is
// what makes the Cloudflare migration a one-file change: swap the backend below and
// nothing above it moves.
//
// Three backends:
//
//   artifact  — the Claude artifact shared database. What the live version runs on.
//   api       — /api/* on Cloudflare Pages, backed by D1. Stub; see functions/.
//   local     — browser storage, single user. The dev fallback so `npm run dev` works.
//
// Collections and their document shapes are documented in docs/DATA-MODEL.md.

import { SEED_ITEMS_BY_ID } from "../data/seed-items.js";
import { SEED_SCHEDULE } from "../data/seed-schedule.js";

export const COLLECTIONS = ["items", "availability", "schedule", "readingChecks", "classOwners"];

const LOCAL_PREFIX = "ioh.local.";

let backend = null; // "artifact" | "api" | "local"
let db = null; // artifact db handle, when backend === "artifact"
let statusMessage = "connecting";

/** collection → { id: doc } */
const cache = Object.fromEntries(COLLECTIONS.map((c) => [c, {}]));
/** collection → Set<handler> */
const listeners = Object.fromEntries(COLLECTIONS.map((c) => [c, new Set()]));

function emit(collection) {
  listeners[collection].forEach((fn) => {
    try {
      fn(cache[collection]);
    } catch (err) {
      console.error("store listener failed for " + collection, err);
    }
  });
}

/**
 * Normalise a snapshot into a plain { id: doc } map.
 *
 * The artifact db may hand back an array of docs, a { docs: [...] } wrapper, or docs
 * whose data lives behind a .data() call. Tolerate all three rather than guessing.
 */
function absorb(snap, keepIdField) {
  const next = {};
  const docs = snap && snap.docs ? snap.docs : snap || [];
  if (!docs || typeof docs.length !== "number") return next;
  for (const doc of docs) {
    const data = doc && typeof doc.data === "function" ? doc.data() : doc;
    const id = (doc && doc.id) || (data && data.id);
    if (id && data) next[id] = keepIdField ? { id, ...data } : data;
  }
  return next;
}

// ── local backend ────────────────────────────────────────────────────────────

function localLoad() {
  for (const c of COLLECTIONS) {
    try {
      cache[c] = JSON.parse(localStorage.getItem(LOCAL_PREFIX + c) || "{}");
    } catch {
      cache[c] = {};
    }
  }
  // First run on this device: fill from the seed so `npm run dev` shows a real app
  // rather than an empty shell. Only when genuinely empty — never clobber edits.
  if (!Object.keys(cache.items).length) {
    cache.items = structuredClone(SEED_ITEMS_BY_ID);
    localSave("items");
  }
  if (!Object.keys(cache.schedule).length) {
    cache.schedule = structuredClone(SEED_SCHEDULE);
    localSave("schedule");
  }
}

function localSave(collection) {
  try {
    localStorage.setItem(LOCAL_PREFIX + collection, JSON.stringify(cache[collection]));
  } catch (err) {
    console.warn("could not persist " + collection + " locally", err);
  }
}

// ── api backend (Cloudflare, not live yet) ───────────────────────────────────

/**
 * Is there a real API behind /api/*?
 *
 * A 200 is not enough. Vite's dev server falls back to index.html for unknown paths,
 * so `/api/health` answers 200 with HTML — which used to make this store pick the `api`
 * backend, fail to parse HTML as JSON, and render an empty app with no fallback.
 * Require JSON with an explicit ok flag, which only the real function returns.
 */
async function apiAvailable() {
  try {
    const res = await fetch("/api/health", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return false;
    if (!(res.headers.get("content-type") || "").includes("application/json")) return false;
    const body = await res.json().catch(() => null);
    return !!(body && body.ok === true);
  } catch {
    return false;
  }
}

async function apiGetAll(collection) {
  const res = await fetch("/api/" + collection, { credentials: "same-origin" });
  if (!res.ok) throw new Error("GET /api/" + collection + " → " + res.status);
  return res.json();
}

async function apiPut(collection, id, doc) {
  const res = await fetch("/api/" + collection + "/" + encodeURIComponent(id), {
    method: "PUT",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(doc),
  });
  if (!res.ok) throw new Error("PUT /api/" + collection + " → " + res.status);
}

async function apiDelete(collection, id) {
  const res = await fetch("/api/" + collection + "/" + encodeURIComponent(id), {
    method: "DELETE",
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error("DELETE /api/" + collection + " → " + res.status);
}

// ── public interface ─────────────────────────────────────────────────────────

/**
 * Resolve a backend and start feeding listeners. Safe to call once at boot.
 * Never throws — an unreachable backend degrades to `local` so the UI still renders.
 *
 * @param {{ prefer?: "artifact"|"api"|"local" }} [opts]
 */
export async function init(opts = {}) {
  const prefer = opts.prefer || (location.protocol === "http:" || location.protocol === "https:" ? "auto" : "local");

  // 1. Artifact database, when running inside a published Claude artifact.
  if (prefer === "auto" || prefer === "artifact") {
    if (typeof window !== "undefined" && window.claude && typeof window.claude.use === "function") {
      try {
        const handle = await window.claude.use("db");
        if (handle) {
          db = handle;
          backend = "artifact";
          statusMessage = "live · shared with the team";
          for (const c of COLLECTIONS) {
            const keepId = c === "items";
            try {
              db.collection(c).onSnapshot((snap) => {
                cache[c] = absorb(snap, keepId);
                emit(c);
              });
            } catch (err) {
              console.warn("could not subscribe to " + c, err);
            }
          }
          return backend;
        }
      } catch (err) {
        console.warn("artifact database unavailable", err);
      }
    }
  }

  // 2. Cloudflare API, when it exists. Absent until Phase 3 ships — fall through then.
  if ((prefer === "auto" || prefer === "api") && (await apiAvailable())) {
    try {
      const loaded = await Promise.all(COLLECTIONS.map((c) => apiGetAll(c)));
      backend = "api";
      statusMessage = "live · shared with the team";
      COLLECTIONS.forEach((c, i) => {
        cache[c] = loaded[i];
        emit(c);
      });
      return backend;
    } catch (err) {
      // Health said yes but a collection would not load. Better to run on local data
      // than to sit on an empty screen — but say so loudly.
      console.error("API answered /api/health but a collection failed to load; using local data", err);
    }
  }

  // 3. Browser storage. Single user, this device only.
  backend = "local";
  statusMessage = "local only · not shared";
  localLoad();
  COLLECTIONS.forEach(emit);
  return backend;
}

/** Subscribe to a collection. Fires immediately with what we already have. */
export function on(collection, handler) {
  listeners[collection].add(handler);
  handler(cache[collection]);
  return () => listeners[collection].delete(handler);
}

/** Current snapshot of a collection: { id: doc }. Treat as read-only. */
export function get(collection) {
  return cache[collection];
}

/**
 * Upsert a document. Updates the cache and notifies listeners immediately, then
 * persists — so the UI never waits on the network. A failed write is logged, not
 * thrown; the next snapshot corrects any drift.
 */
export async function set(collection, id, doc) {
  cache[collection][id] = doc;
  emit(collection);
  try {
    if (backend === "artifact") await db.doc(collection + "/" + id).set(doc);
    else if (backend === "api") await apiPut(collection, id, doc);
    else localSave(collection);
  } catch (err) {
    console.error("failed to save " + collection + "/" + id, err);
  }
}

export async function remove(collection, id) {
  delete cache[collection][id];
  emit(collection);
  try {
    if (backend === "artifact") await db.doc(collection + "/" + id).delete();
    else if (backend === "api") await apiDelete(collection, id);
    else localSave(collection);
  } catch (err) {
    console.error("failed to delete " + collection + "/" + id, err);
  }
}

export function status() {
  return {
    backend,
    online: backend === "artifact" || backend === "api",
    shared: backend === "artifact" || backend === "api",
    message: statusMessage,
  };
}
